import type { StyleRow } from './stylesFixtureData';
import { groups, brandOptions, seasons } from './stylesFixtureData';

/**
 * Additive fixtures for epic DL-66948 (Testmo folder dl-59304, cross-cutting third: Supplier
 * column sorting, the end-to-end season save, and the bulk-edit-side regression guard). New file
 * — nothing existing in `styles.ts` is edited beyond one import + two `stylesData.push(...)`
 * lines, mirroring the established `stylesBulkEditProbes` pattern (CLAUDE.md §4 /
 * `no-new-sprocs.md`'s sibling "additive mocks only" rule).
 */

const baseRow = (
  overrides: Partial<StyleRow> & Pick<StyleRow, 'id' | 'name' | 'customId'>
): StyleRow => ({
  description: '',
  state: 'unpublished',
  logo: '',
  season: null,
  group: groups[0],
  brand: {
    id: brandOptions[0].id,
    customId: brandOptions[0].userDefinedId ?? '',
    name: brandOptions[0].name,
    contactPersonId: '',
    contactPersonName: '',
  },
  supplier: null,
  categories: [],
  styleColors: [],
  customFields: [],
  properties: [],
  lastUpdated: '2026-02-15T09:00:00',
  readyForExport: false,
  ...overrides,
});

/**
 * DL-67014 (Testmo #1807782 — the whole dl-59304 folder's spine). Two styles with an OLD season
 * (`AW25`, `seasons[0]` — the "no/old season" precondition, a real pre-hotfix value), both
 * `unpublished` (never trimmed by `BulkEditWarningModal`) and supplier-free (this story never
 * touches a supplier field, per its own acceptance criteria). The bulk-edit target is `AW26` — the
 * same season `update-execution-and-feedback.spec.ts` already proved reachable via the real Season
 * combobox; reused here rather than re-derived (dl-66948's own ground rule: "do not re-derive
 * dl-66944's 1807767 option-list contract — finding the season is a precondition here"). Both rows
 * share a real, always-selectable season (rather than `null`/a synthetic one) so the spec's own
 * teardown can restore it through the same real UI interaction the main assertion uses.
 */
const crossCuttingSeasonSpineStyles = (): StyleRow[] => [
  baseRow({
    id: 97501,
    name: 'CrossCuttingSpine SeasonProbe A',
    customId: 'ST-97501',
    season: seasons[0],
  }),
  baseRow({
    id: 97502,
    name: 'CrossCuttingSpine SeasonProbe B',
    customId: 'ST-97502',
    season: seasons[0],
  }),
];

/**
 * DL-67016 (Testmo #1807785) — the bulk-edit-side regression guard ("bulk editing non-supplier
 * fields is unaffected by the supplier separation change"). The Testmo case's title names
 * "Status", but Styles' bulk-edit modal has NO State/Status field at all —
 * `styleFieldAccess.state.excludeFromBulkEdit = true`, already confirmed dead in
 * `pages/styles/bulk-edit-actions/state-field-not-editable.spec.ts` — so this substitutes Brand: a
 * genuinely non-supplier, always-bulk-editable field the mock's PUT handler resolves correctly by
 * id post-submit (unlike Group, whose `fieldTransforms.group` has the same "reads `.name` off a
 * label-only `ComboBoxOption`" gap Season/Brand/Supplier have, but — unlike them — no compensating
 * id-resolution in the PUT handler, so it would round-trip a blank name; not used here to avoid
 * exercising that unrelated, unconfirmed gap). Both rows share one synthetic starting brand (never
 * one of the ambient `/api/brands` faker-generated names, which are random per test run and would
 * make a hardcoded target brand name flaky) and carry no supplier/contact at all, so neither
 * Brand's nor Supplier contact's `bulkEditDisabledWhen` gate can fire. The spec picks whatever
 * REAL option the Brand dropdown offers that differs from this starting name (the same
 * identity-agnostic `pickDifferentOption` technique `bulkEditStylesGeneralFields.helpers.ts`
 * already uses), rather than asserting a specific target brand name.
 */
const BRAND_REGRESSION_STARTING_BRAND = {
  id: 97701,
  customId: 'BR-97701',
  name: 'CrossCuttingSpine Brand Origin',
  contactPersonId: '',
  contactPersonName: '',
};

const crossCuttingBrandRegressionStyles = (): StyleRow[] => [
  baseRow({
    id: 97503,
    name: 'CrossCuttingSpine BrandProbe A',
    customId: 'ST-97503',
    brand: BRAND_REGRESSION_STARTING_BRAND,
  }),
  baseRow({
    id: 97504,
    name: 'CrossCuttingSpine BrandProbe B',
    customId: 'ST-97504',
    brand: BRAND_REGRESSION_STARTING_BRAND,
  }),
];

/**
 * DL-67009 (Testmo #1807776) — Styles table Supplier-column sort. Two supplier names placed
 * deliberately outside the ambient dataset's known alphabet range (Guangzhou/Ningbo/Shanghai —
 * `stylesFixtureData.ts` — and "Zero Contact Supplier" — `stylesBulkEditProbeFixtureData.ts`) so
 * an ascending sort's first row and a descending sort's first row are deterministic regardless of
 * what other epics' fixtures pin into the shared dataset later (the exact ambient-contamination
 * trap dl-66940's PR body already documented for this same shared array).
 */
const crossCuttingSupplierSortStyles = (): StyleRow[] => [
  baseRow({
    id: 97505,
    name: 'CrossCuttingSpine SortProbe Alpha',
    customId: 'ST-97505',
    supplier: {
      id: 97601,
      customId: 'S-97601',
      name: 'Aabenraa Textile Works',
      contactPersonId: '',
      contactPersonName: '',
    },
  }),
  baseRow({
    id: 97506,
    name: 'CrossCuttingSpine SortProbe Omega',
    customId: 'ST-97506',
    supplier: {
      id: 97602,
      customId: 'S-97602',
      name: 'Zzyzx Garment Mills',
      contactPersonId: '',
      contactPersonName: '',
    },
  }),
];

/** Single entry point `styles.ts` pushes onto `stylesData` — keeps that already-800+-line file's
 * own footprint from this epic down to one import + one push line. */
export const crossCuttingFixtureStyles = (): StyleRow[] => [
  ...crossCuttingSeasonSpineStyles(),
  ...crossCuttingBrandRegressionStyles(),
  ...crossCuttingSupplierSortStyles(),
];
